import React, { FC, UIEvent, useState } from 'react';
import classNames from 'classnames';
import s from './Overlay.scss';
import { useStyles } from '@wix/tpa-settings/react';
import stylesParams from '../../../../../../stylesParams';
import { PlayButton } from '../../../../common/components/PlayButton/PlayButton';
import { Button } from '../../../../common/components/Button/Button';
import { LiveLabel } from '../../../../common/components/LiveLabel/LiveLabel';
import { Playable } from '../../../../common/components/Player/players/Playable/Playable';
import { usePlayback } from '../../../../hooks';
import { Picture } from '../../../../common/components/Picture/Picture';
import { PlayNextButton } from '../PlayNextButton/PlayNextButton';
import { useEnterOrSpaceKeyDown } from '../../../../hooks/useEnterOrSpaceKeyDown';

type Props = {
  playButtonText?: string;
  paidButton?: {
    text: string;
    handler(): void;
  };

  content: React.ReactNode;
  buttons?: React.ReactNode;

  coverUrl?: string;
  videoUrl?: string;
};

const useOverlaySettings = () => {
  const styles = useStyles();

  const opacity = styles.get(stylesParams.mainItemOverlayOpacity) / 100;
  return { opacity };
};

const ButtonsWrapper: FC = ({ children }) => {
  // call e.stopPropagation() to prevent calling onClick and onKeyDown on OverlayContent
  const stopPropagation = (e: UIEvent) => e.stopPropagation();

  return (
    <div
      className={s.buttons}
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
    >
      {children}
    </div>
  );
};

const OverlayContent: FC<{ children: React.ReactNode; ariaLabel?: string }> = ({
  children,
  ariaLabel,
}) => {
  const { opacity } = useOverlaySettings();
  const { play } = usePlayback();
  const handleEnterOrSpace = useEnterOrSpaceKeyDown(play);

  return (
    <div
      className={s.content}
      style={{
        backgroundColor: `rgba(0,0,0,${opacity})`,
      }}
      role="button"
      tabIndex={0}
      onClick={play}
      onKeyDown={handleEnterOrSpace}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
};

export const OverlayHeaderTitle: FC<{ children: string }> = ({ children }) => {
  return (
    <h3 className={s.title} data-hook="classic-overlay-title">
      {children}
    </h3>
  );
};

export const OverlayHeader: FC<{
  children: React.ReactNode;
}> = ({ children }) => <header className={s.overlayHeader}>{children}</header>;

export const Overlay: FC<Props> = ({
  playButtonText,
  paidButton,
  videoUrl,
  coverUrl,
  content,
  buttons,
}) => {
  const [playing, setPlaying] = useState(false);

  return (
    <section className={s.root}>
      {videoUrl && (
        <Playable
          src={videoUrl}
          playing
          muted
          loop
          controlsHidden
          onPlay={() => setPlaying(true)}
        />
      )}
      {coverUrl && (
        <Picture
          className={classNames(s.picture, {
            [s.hidden]: coverUrl && playing,
          })}
          src={coverUrl}
          breakpoints={[
            {
              min: 0,
              width: 768,
              height: 432,
            },
          ]}
        />
      )}
      <OverlayContent ariaLabel={playButtonText}>
        {content}
        <ButtonsWrapper>
          {playButtonText && <PlayButton>{playButtonText}</PlayButton>}
          {paidButton && (
            <Button
              data-hook="classic-overlay-paid-button"
              onClick={paidButton.handler}
            >
              {paidButton.text}
            </Button>
          )}
          {buttons}
        </ButtonsWrapper>
      </OverlayContent>
    </section>
  );
};
